import { type Database } from "@fitness-app/supabase";

// TRE-1611 (Low-ticket 2.0, F3.1) — configurable upsell offers surfaced to clients
// in the mobile app (home / celebration / advisor / end-of-trial). Rows are managed
// by the admin; products-service GET /offers and the client advisor tool read them.

export type ClientOfferEntity = Database["public"]["Tables"]["client_offer"]["Row"];

export enum ClientOfferType {
  SelfCheckout = "selfCheckout",
  Consultation = "consultation",
}

export enum ClientOfferTouchpoint {
  Home = "home",
  Celebration = "celebration",
  Advisor = "advisor",
  EndOfTrial = "endOfTrial",
}

/**
 * TRE-2181 — step of the upgrade path an offer belongs to. The home stepper lists
 * low-ticket offers (Klucz -> Bikini) before high-ticket ones (Program Hollywood; the
 * phone consultation counts as high ticket because that is where it leads). The tier
 * also picks the Meta event sent on tap: Lead for low ticket, LeadQualified for high.
 */
export enum ClientOfferTier {
  LowTicket = "lowTicket",
  HighTicket = "highTicket",
}

/** Typed view over client_offer with the jsonb columns narrowed. */
export interface ClientOffer extends Omit<ClientOfferEntity, "type" | "tier" | "productIds" | "touchpoints"> {
  type: ClientOfferType;
  tier: ClientOfferTier;
  /** Product ids this offer targets; null = the offer applies to every product. */
  productIds: string[] | null;
  /** Touchpoints where the offer may surface; null = every touchpoint. */
  touchpoints: string[] | null;
}

/** The client-facing projection returned by products-service GET /offers. */
export interface ClientOfferView {
  id: string;
  title: string;
  description: string;
  price: string | null;
  type: ClientOfferType;
  tier: ClientOfferTier;
  url: string;
  badge: string | null;
}

/** Response of GET /offers: the matched offers plus where the client stands on the path. */
export interface ClientOffersResponse {
  offers: ClientOfferView[];
  /** Tier of the client's current (highest) product; null when the product has no tier (add-on, content). */
  currentTier: ClientOfferTier | null;
  /** Name of the product that sets `currentTier`, for the "you are here" step. */
  currentProductName: string | null;
}

/** Meta Conversions API event sent when a client taps a step of the offer path (TRE-2182). */
export const META_EVENT_BY_OFFER_TIER: Record<ClientOfferTier, "Lead" | "LeadQualified"> = {
  [ClientOfferTier.LowTicket]: "Lead",
  [ClientOfferTier.HighTicket]: "LeadQualified",
};
